import React, { useEffect, useState, useCallback } from "react";
import { X, Box, User, Truck, Paperclip, Info, MapPin, Loader2, Pencil, Plus, Download, ExternalLink, Save, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { TicketStatusBadge } from "@/components/TicketStatusBadge";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import NotesHistoryModal from "./NotesHistoryModal";

const selectClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const textareaClass = "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function Section({ icon: Icon, title, action, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Icon className="h-4 w-4 text-slate-500" /> {title}
        </div>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-slate-900">{value || "N/A"}</p>
    </div>
  );
}

function formatAddress(a) {
  if (!a) return null;
  if (a.warehouse_name) return `${a.warehouse_name}\n${[a.street, a.city, a.state, a.zip, a.country].filter(Boolean).join(", ")}`;
  return [a.street, a.city, a.state, a.zip, a.country].filter(Boolean).join(", ") || null;
}

export default function TicketDetailPanel({ ticketId, open, onClose, onUpdated }) {
  const { user } = useAuth();
  const isStaff = user?.role !== "client_user";
  // Per product spec, client_users have the same ticket capabilities as staff for tickets
  // they can see (org-scoped visibility is enforced server-side). They can update status,
  // add notes, manage CC recipients, upload attachments, and delete tickets.
  const canEdit = !!user;
  const canDelete = !!user;
  const [ticket, setTicket] = useState(null);
  const [notes, setNotes] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [meta, setMeta] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusVal, setStatusVal] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);
  const [pendingWithId, setPendingWithId] = useState("");
  const [noteText, setNoteText] = useState("");
  const [notesOpen, setNotesOpen] = useState(false);
  const [editingCc, setEditingCc] = useState(false);
  const [ccDraft, setCcDraft] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    try {
      const [t, n, a, m, u] = await Promise.all([
        api.get(`/tickets/${ticketId}`),
        api.get(`/tickets/${ticketId}/notes`),
        api.get(`/tickets/${ticketId}/attachments`),
        api.get("/tickets/meta"),
        api.get("/users/assignable").catch(() => ({ data: [] })),
      ]);
      setTicket(t.data);
      setNotes(n.data);
      setAttachments(a.data);
      setMeta(m.data);
      setUsers(u.data || []);
      setStatusVal(t.data.status);
      setPendingWithId((t.data.pending_with || {}).user_id || "");
    } catch (e) { setError(apiError(e)); } finally { setLoading(false); }
  }, [ticketId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  async function saveStatus() {
    setStatusSaving(true); setError(null);
    try {
      const payload = { status: statusVal, note: statusNote || null };
      const currentPwId = (ticket?.pending_with || {}).user_id || "";
      if (pendingWithId !== currentPwId) {
        payload.pending_with = pendingWithId ? { user_id: pendingWithId, name: null } : { user_id: null, name: null };
      }
      await api.post(`/tickets/${ticketId}/status`, payload);
      setStatusNote("");
      await load(); onUpdated && onUpdated();
    } catch (e) { setError(apiError(e)); } finally { setStatusSaving(false); }
  }

  async function addNote() {
    if (!noteText.trim()) return;
    try {
      await api.post(`/tickets/${ticketId}/notes`, { text: noteText });
      setNoteText(""); await load();
    } catch (e) { setError(apiError(e)); }
  }

  function startEditCc() { setCcDraft([...(ticket.email_cc || []), ""]); setEditingCc(true); }
  async function saveCc() {
    try {
      await api.put(`/tickets/${ticketId}/email-cc`, { email_cc: ccDraft.filter(Boolean) });
      setEditingCc(false); await load();
    } catch (e) { setError(apiError(e)); }
  }

  async function uploadFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const token = localStorage.getItem("dolphin_token");
      await fetch(`${API}/tickets/${ticketId}/attachments`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: form });
      await load();
    } catch (err) { setError("Upload failed"); } finally { setUploading(false); e.target.value = ""; }
  }

  function downloadUrl(att) {
    const token = localStorage.getItem("dolphin_token");
    return `${API}/tickets/${ticketId}/attachments/${att.id}/download?token=${token}`;
  }

  async function deleteTicket() {
    setDeleting(true); setError(null);
    try {
      await api.delete(`/tickets/${ticketId}`);
      onUpdated && onUpdated();
      onClose();
    } catch (e) { setError(apiError(e)); } finally { setDeleting(false); }
  }

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
      <div className="fixed right-0 top-0 z-50 flex h-full w-full max-w-3xl flex-col bg-slate-50 shadow-2xl">
        <div className="flex items-center justify-between border-b bg-white px-6 py-4">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold tracking-tight">{ticket?.ticket_code || "Ticket"}</h2>
            {ticket && <TicketStatusBadge status={ticket.status} />}
          </div>
          <div className="flex items-center gap-2">
            {canDelete && ticket && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="text-rose-700 hover:bg-rose-50 hover:text-rose-800">
                    <Trash2 className="h-4 w-4" /> Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this ticket?</AlertDialogTitle>
                    <AlertDialogDescription>
                      <span className="block">You are about to permanently delete <span className="font-semibold text-slate-900">{ticket.ticket_code}</span> — {ticket.ticket_type}.</span>
                      <span className="mt-2 block text-rose-600">This will also remove all of its notes and attachments. This action cannot be undone.</span>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      disabled={deleting}
                      onClick={(e) => { e.preventDefault(); deleteTicket(); }}
                      className="bg-rose-600 text-white hover:bg-rose-700"
                    >
                      {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
                      Yes, delete ticket
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-slate-100"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
          {loading || !ticket ? <p className="text-sm text-muted-foreground">Loading…</p> : (
            <>
              <div className="grid gap-4 lg:grid-cols-2">
                <Section icon={Box} title="Product Information">
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Asset ID" value={ticket.asset_id} />
                    <Field label="Product Type" value={ticket.product_type} />
                    <Field label="Site ID" value={ticket.site_id} />
                    <Field label="Site Phone" value={ticket.site_phone} />
                    <div className="col-span-2"><Field label="Serial Number" value={ticket.serial_number} /></div>
                  </div>
                </Section>
                <Section icon={Info} title="Status & Assignment">
                  <div className="space-y-3">
                    {canEdit && meta ? (
                      <>
                        <div>
                          <p className="mb-1 text-xs uppercase tracking-wide text-slate-500">Status</p>
                          <select className={selectClass} value={statusVal} onChange={(e) => setStatusVal(e.target.value)}>
                            {meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        <div>
                          <p className="mb-1 text-xs uppercase tracking-wide text-slate-500">Pending With</p>
                          <select
                            className={selectClass}
                            value={pendingWithId}
                            onChange={(e) => setPendingWithId(e.target.value)}
                            data-testid="pending-with-select"
                          >
                            <option value="">Unassigned</option>
                            {users.map((u) => (
                              <option key={u.id} value={u.id}>
                                {u.full_name}{u.role ? ` — ${u.role === "client_user" ? (u.client_name || "Organization") : (u.role === "admin" ? "Admin" : "Warehouse Staff")}` : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                        <textarea rows={2} className={textareaClass} value={statusNote} onChange={(e) => setStatusNote(e.target.value)} placeholder="Optional note about this status change…" />
                        <Button
                          size="sm"
                          data-testid="update-status-btn"
                          disabled={statusSaving || (statusVal === ticket.status && pendingWithId === ((ticket.pending_with || {}).user_id || "") && !statusNote.trim())}
                          onClick={saveStatus}
                        >
                          {statusSaving && <Loader2 className="h-4 w-4 animate-spin" />} Save Changes
                        </Button>
                      </>
                    ) : (
                      <>
                        <TicketStatusBadge status={ticket.status} />
                        <div className="border-t pt-3">
                          <p className="text-xs uppercase tracking-wide text-slate-500">Pending With</p>
                          <p className="mt-1 text-sm font-medium">{(ticket.pending_with || {}).name || "Unassigned"}</p>
                        </div>
                      </>
                    )}
                    <div>
                      <p className="text-xs uppercase tracking-wide text-slate-500">Ticket Type</p>
                      <p className="mt-1 text-sm font-medium">{ticket.ticket_type}</p>
                    </div>
                  </div>
                </Section>
              </div>
              {ticket.ticket_info && (
                <Section icon={Info} title="Product Details">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{ticket.ticket_info}</p>
                </Section>
              )}
              <Section icon={User} title="Email CC Recipients" action={canEdit && !editingCc && <Button variant="ghost" size="sm" onClick={startEditCc}><Pencil className="h-3.5 w-3.5" /></Button>}>
                {editingCc ? (
                  <div className="space-y-2">
                    {ccDraft.map((e, i) => (
                      <div key={i} className="flex gap-2">
                        <Input value={e} onChange={(ev) => setCcDraft((d) => d.map((x, idx) => idx === i ? ev.target.value : x))} placeholder="email@example.com" />
                        <Button variant="ghost" size="icon" onClick={() => setCcDraft((d) => d.filter((_, idx) => idx !== i))}><X className="h-4 w-4" /></Button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setCcDraft((d) => [...d, ""])}><Plus className="h-3.5 w-3.5" /> Add</Button>
                      <Button size="sm" onClick={saveCc}><Save className="h-3.5 w-3.5" /> Save</Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditingCc(false)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {(ticket.email_cc || []).length === 0 ? <p className="text-sm text-muted-foreground">None</p> :
                      (ticket.email_cc || []).map((e) => <Badge key={e} variant="secondary" className="bg-sky-50 text-sky-800 border-sky-200">{e}</Badge>)}
                  </div>
                )}
              </Section>
              <div className="grid gap-4 lg:grid-cols-2">
                <Section icon={MapPin} title="From Address">
                  <p className="whitespace-pre-line text-sm text-slate-700">{formatAddress(ticket.from_address) || "N/A"}</p>
                </Section>
                <Section icon={MapPin} title="To Address">
                  <p className="whitespace-pre-line text-sm text-slate-700">{formatAddress(ticket.to_address) || "N/A"}</p>
                </Section>
              </div>
              <Section icon={Truck} title="Shipping Details">
                <div className="grid gap-4 sm:grid-cols-2">
                  <ShippingBlock label="Outbound Shipping" data={ticket.outbound_shipping} />
                  <ShippingBlock label="Return Shipping" data={ticket.return_shipping} />
                </div>
              </Section>
              <Section icon={Paperclip} title={`Attachments (${attachments.length})`} action={canEdit && (
                <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50">
                  {uploading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}<Plus className="h-3.5 w-3.5" /> Upload
                  <input type="file" className="hidden" onChange={uploadFile} />
                </label>
              )}>
                {attachments.length === 0 ? <p className="text-sm text-muted-foreground">No attachments.</p> : (
                  <div className="space-y-2">
                    {attachments.map((a) => (
                      <div key={a.id} className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50/50 p-3">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{a.filename}</p>
                          <p className="text-xs text-slate-500">{(a.size / 1024).toFixed(1)} KB · {new Date(a.uploaded_at).toLocaleString()}</p>
                        </div>
                        <a href={downloadUrl(a)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700">
                          <Download className="h-3.5 w-3.5" /> Download
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </Section>
              <Section icon={Info} title="Notes History" action={<Button variant="outline" size="sm" onClick={() => setNotesOpen(true)}>View All ({notes.length})</Button>}>
                <div className="space-y-3">
                  {notes.slice(0, 3).map((n) => (
                    <div key={n.id} className="border-l-2 border-slate-200 pl-3">
                      <p className="text-sm font-medium text-slate-900">{n.author_name || "System"}</p>
                      <p className="mt-0.5 whitespace-pre-line text-sm text-slate-700">{n.text}</p>
                      <p className="mt-0.5 text-xs text-slate-500">{new Date(n.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                  {canEdit && (
                    <div className="border-t pt-3">
                      <textarea rows={2} className={textareaClass} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note…" />
                      <Button size="sm" className="mt-2" onClick={addNote} disabled={!noteText.trim()}>Add Note</Button>
                    </div>
                  )}
                </div>
              </Section>
            </>
          )}
        </div>
      </div>
      <NotesHistoryModal open={notesOpen} onClose={() => setNotesOpen(false)} ticketCode={ticket?.ticket_code} notes={notes} />
    </>
  );
}

function ShippingBlock({ label, data }) {
  const d = data || {};
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <div className="rounded-md border border-slate-200 bg-slate-50/50 p-3 space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs text-slate-500">Tracking Number</p>
            <p className="truncate text-sm font-mono font-medium text-slate-900">{d.tracking || "N/A"}</p>
          </div>
          {d.tracking && d.track_url && (
            <a href={d.track_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-md bg-amber-400 px-3 py-1.5 text-xs font-semibold text-amber-950 hover:bg-amber-500">
              Track Package <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        <div>
          <p className="text-xs text-slate-500">Carrier</p>
          <p className="text-sm font-medium text-slate-900">{d.carrier || "N/A"}</p>
        </div>
      </div>
    </div>
  );
}
